import React from 'react';
import { injectIntl } from 'react-intl';
import { Plus } from 'lucide-react';
import type { IntlLike } from '../clientPresentation';

/**
 * 新增客戶, as an EXTENDED floating action button — where V1 puts it
 * (clientPage.js:2616, `.fab` fixed bottom-right above the footer nav).
 *
 * It used to sit in the tab row, which meant an adviser who had scrolled into a
 * 185-client list had to scroll back to the top to add one. V1's own FAB is a
 * speed dial with three secondary actions; this is the single primary action
 * only, because ClientV2 already exposes 匯入客戶 and 潛在客戶 in the command
 * bar and a second route to them would be duplication.
 *
 * `bottom` clears the legacy footer nav, which is fixed on every viewport below
 * the side-nav breakpoint — the same offsets V1's `.fab` uses, including the
 * safe-area variable for notched devices.
 *
 * It carries its label rather than the bare ＋ it started as. A circle's only
 * name was `aria-label` and `title` — the first is inaudible to someone who can
 * see, and the second never surfaces on touch, which is the platform this is
 * mostly used on. So on a phone it was an unexplained blue circle over a list
 * that has nothing else to do with adding. `aria-label` stays even though the
 * text is visible: both come from the same message id, so they cannot disagree,
 * and the name survives if the label is ever collapsed at a breakpoint.
 */
export type ClientAddFabProps = {
  onAddClient?: () => void;
  /**
   * Set while the detail drawer is open. The FAB is fixed to the viewport and
   * the drawer overlays the same corner, so it would otherwise float on top of
   * the drawer's content. Kept mounted and faded out rather than unmounted, so
   * it animates back in alongside the drawer's own slide-out instead of
   * popping back the instant the client is deselected.
   */
  hidden?: boolean;
  intl: IntlLike;
};

const ClientAddFab = ({ onAddClient, hidden, intl }: ClientAddFabProps) => {
  const label = intl.formatMessage({ id: 'clientv2-add-client' });

  return (
    <button
      type="button"
      data-testid="client-add"
      className={`clientv2-add-fab${hidden ? ' is-hidden' : ''}`}
      aria-label={label}
      title={label}
      // Out of the tab order and out of the a11y tree while it is not visible,
      // or it stays reachable behind the drawer.
      aria-hidden={hidden || undefined}
      tabIndex={hidden ? -1 : undefined}
      onClick={() => onAddClient?.()}
    >
      <Plus size={22} aria-hidden="true" />
      <span className="clientv2-add-fab-label">{label}</span>
    </button>
  );
};

export default injectIntl(ClientAddFab);
